import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import {
  Plus, Trash2, Pencil, Eye, EyeOff, Bold, Italic, Heading2, Heading3,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Quote, Code,
  Save, X, ArrowLeft, FileText, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import NativeSelect from "@/components/forms/NativeSelect";
import ImageUploader from "@/components/forms/ImageUploader";
import { toast } from "sonner";
import api from "@/lib/api";

// Categories are defined server-side; kept in sync via the same list.
const BLOG_CATEGORIES = [
  "Spa Treatments",
  "Wellness Tips",
  "Ayurvedic Therapy",
  "Massage Benefits",
  "Pimpri-Chinchwad Spa Guide",
  "Pune Spa Guide",
];

/** Minimal markdown toolbar. Wraps selected text with the given `left`/`right`
 *  tokens (or inserts a prefix at the start of the selected lines for lists/
 *  headings). Focuses back into the textarea so the user can keep typing. */
function useToolbar(textareaRef, value, setValue) {
  const wrap = useCallback((left, right = "") => {
    const el = textareaRef.current; if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const before = value.slice(0, s), sel = value.slice(s, e), after = value.slice(e);
    const inserted = `${left}${sel || "text"}${right}`;
    const next = `${before}${inserted}${after}`;
    setValue(next);
    // Restore selection just around the inserted text
    requestAnimationFrame(() => {
      el.focus();
      const newStart = s + left.length;
      const newEnd = newStart + (sel || "text").length;
      el.setSelectionRange(newStart, newEnd);
    });
  }, [textareaRef, value, setValue]);

  const prefixLines = useCallback((prefix) => {
    const el = textareaRef.current; if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const before = value.slice(0, s);
    // Expand selection to full lines
    const lineStart = before.lastIndexOf("\n") + 1;
    const afterFromEnd = value.slice(e);
    const lineEndOffset = afterFromEnd.indexOf("\n");
    const lineEnd = lineEndOffset === -1 ? value.length : e + lineEndOffset;
    const chunk = value.slice(lineStart, lineEnd);
    const prefixed = chunk.split("\n").map(l => (l.startsWith(prefix) ? l : `${prefix}${l}`)).join("\n");
    const next = `${value.slice(0, lineStart)}${prefixed}${value.slice(lineEnd)}`;
    setValue(next);
    requestAnimationFrame(() => el.focus());
  }, [textareaRef, value, setValue]);

  return { wrap, prefixLines };
}

/** New / Edit form. Handles both create and update — controlled by `initial`. */
function BlogEditor({ initial, onSaved, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [category, setCategory] = useState(initial?.category || BLOG_CATEGORIES[0]);
  const [excerpt, setExcerpt] = useState(initial?.excerpt || "");
  const [contentMd, setContentMd] = useState(initial?.content_md || "");
  const [coverImage, setCoverImage] = useState(initial?.cover_image || "");
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [author, setAuthor] = useState(initial?.author || "Pune Spa Guide Editorial");
  const [postStatus, setPostStatus] = useState(initial?.status || "draft");
  const [seoTitle, setSeoTitle] = useState(initial?.seo_title || "");
  const [seoDescription, setSeoDescription] = useState(initial?.seo_description || "");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const { wrap, prefixLines } = useToolbar(textareaRef, contentMd, setContentMd);

  const previewHtml = useMemo(() => {
    if (!contentMd) return `<p class="text-[#9CA3AF] italic">Nothing to preview yet — start typing on the left.</p>`;
    const raw = marked.parse(contentMd, { async: false, breaks: true, gfm: true });
    return DOMPurify.sanitize(raw);
  }, [contentMd]);

  const wordCount = useMemo(() => contentMd.split(/\s+/).filter(Boolean).length, [contentMd]);
  const readMin = Math.max(1, Math.round(wordCount / 200));

  const save = async () => {
    if (!title.trim() || !contentMd.trim()) {
      return toast.error("Title and content are required.");
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        category,
        excerpt: excerpt.trim(),
        content_md: contentMd,
        cover_image: coverImage || "",
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        author: author.trim(),
        status: postStatus,
        seo_title: seoTitle.trim() || undefined,
        seo_description: seoDescription.trim() || undefined,
      };
      const res = initial?.id
        ? await api.put(`/admin/blog/posts/${initial.id}`, payload)
        : await api.post("/admin/blog/posts", payload);
      toast.success(`Post ${initial?.id ? "updated" : "created"} — ${res.data.status === "published" ? "live" : "saved as draft"}`);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const insertImage = () => {
    const url = window.prompt("Enter image URL:");
    if (url) wrap(`\n![image](${url})\n`, "");
  };
  const insertLink = () => {
    const url = window.prompt("Enter link URL:");
    if (url) wrap("[", `](${url})`);
  };

  return (
    <div className="space-y-5" data-testid="admin-blog-editor">
      {/* Header actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={onCancel} className="text-sm text-[#4B5563] hover:text-[#1F4E3D] inline-flex items-center gap-1" data-testid="blog-editor-back">
          <ArrowLeft size={14} /> Back to posts
        </button>
        <div className="flex items-center gap-2">
          <NativeSelect
            value={postStatus}
            onChange={(v) => setPostStatus(v)}
            data-testid="blog-editor-status"
            className="h-9"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </NativeSelect>
          <Button onClick={save} disabled={saving} data-testid="blog-editor-save-button" className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2">
            {saving ? "Saving…" : (<><Save size={14}/> Save</>)}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Main editor pane */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 5 Benefits of Regular Ayurvedic Massage"
              data-testid="blog-editor-title"
              className="text-lg"
            />
          </div>
          <div>
            <Label className="text-xs">Excerpt (shown in listings)</Label>
            <Textarea
              rows={2}
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value.slice(0, 400))}
              placeholder="One-sentence hook that appears in the blog index & meta description."
              data-testid="blog-editor-excerpt"
            />
            <div className="text-[10px] text-right text-[#7A5A22] mt-0.5">{excerpt.length}/400</div>
          </div>

          <div>
            <Label className="text-xs mb-1 block">Content *</Label>
            <Tabs defaultValue="write" className="w-full">
              <TabsList data-testid="blog-editor-tabs">
                <TabsTrigger value="write" data-testid="blog-editor-tab-write"><FileText size={14} className="mr-1" /> Write</TabsTrigger>
                <TabsTrigger value="preview" data-testid="blog-editor-tab-preview"><Eye size={14} className="mr-1" /> Preview</TabsTrigger>
              </TabsList>
              <TabsContent value="write" className="space-y-2">
                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-1 border border-[#E7DDCF] bg-[#FBF7F1] rounded-md p-1" data-testid="blog-editor-toolbar">
                  <ToolbarBtn title="Heading 2" onClick={() => prefixLines("## ")}><Heading2 size={14}/></ToolbarBtn>
                  <ToolbarBtn title="Heading 3" onClick={() => prefixLines("### ")}><Heading3 size={14}/></ToolbarBtn>
                  <div className="w-px h-5 bg-[#E7DDCF] mx-1" />
                  <ToolbarBtn title="Bold" onClick={() => wrap("**", "**")}><Bold size={14}/></ToolbarBtn>
                  <ToolbarBtn title="Italic" onClick={() => wrap("_", "_")}><Italic size={14}/></ToolbarBtn>
                  <ToolbarBtn title="Inline code" onClick={() => wrap("`", "`")}><Code size={14}/></ToolbarBtn>
                  <div className="w-px h-5 bg-[#E7DDCF] mx-1" />
                  <ToolbarBtn title="Bullet list" onClick={() => prefixLines("- ")}><List size={14}/></ToolbarBtn>
                  <ToolbarBtn title="Numbered list" onClick={() => prefixLines("1. ")}><ListOrdered size={14}/></ToolbarBtn>
                  <ToolbarBtn title="Quote" onClick={() => prefixLines("> ")}><Quote size={14}/></ToolbarBtn>
                  <div className="w-px h-5 bg-[#E7DDCF] mx-1" />
                  <ToolbarBtn title="Insert link" onClick={insertLink}><LinkIcon size={14}/></ToolbarBtn>
                  <ToolbarBtn title="Insert image" onClick={insertImage}><ImageIcon size={14}/></ToolbarBtn>
                </div>
                <Textarea
                  ref={textareaRef}
                  value={contentMd}
                  onChange={(e) => setContentMd(e.target.value)}
                  rows={20}
                  placeholder="Write your post in Markdown. Use the toolbar above for quick formatting, or type raw markdown."
                  className="font-mono text-sm"
                  data-testid="blog-editor-content"
                />
                <div className="text-[11px] text-[#7A5A22] flex justify-between">
                  <span>{wordCount} words · ~{readMin} min read</span>
                  <span>Markdown supported (headings, lists, links, images, code, quotes)</span>
                </div>
              </TabsContent>
              <TabsContent value="preview">
                <div
                  className="prose-content border border-[#E7DDCF] rounded-md p-5 bg-white min-h-[400px]"
                  data-testid="blog-editor-preview"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Sidebar — metadata */}
        <aside className="space-y-4">
          <div className="rounded-xl bg-white border border-[#E7DDCF] p-4 space-y-3">
            <div>
              <Label className="text-xs">Category *</Label>
              <NativeSelect value={category} onChange={(v) => setCategory(v)} data-testid="blog-editor-category">
                {BLOG_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">Slug (URL — leave blank to auto-generate)</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. 5-benefits-ayurvedic-massage" data-testid="blog-editor-slug" />
              {slug && (
                <div className="text-[10px] text-[#7A5A22] mt-0.5">URL will be: /blog/{slug}</div>
              )}
            </div>
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="wellness, massage, pune" data-testid="blog-editor-tags" />
            </div>
            <div>
              <Label className="text-xs">Author</Label>
              <Input value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="blog-editor-author" />
            </div>
          </div>

          <div className="rounded-xl bg-white border border-[#E7DDCF] p-4">
            <Label className="text-xs mb-2 block">Cover image</Label>
            <ImageUploader
              value={coverImage}
              onChange={setCoverImage}
              aspectRatio="wide"
              testId="blog-editor-cover"
            />
          </div>

          <div className="rounded-xl bg-white border border-[#E7DDCF] p-4 space-y-3">
            <div className="font-medium text-xs uppercase tracking-widest text-[#7A5A22]">SEO (optional)</div>
            <div>
              <Label className="text-xs">SEO title (defaults to post title)</Label>
              <Input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={70} data-testid="blog-editor-seo-title" />
            </div>
            <div>
              <Label className="text-xs">Meta description (defaults to excerpt)</Label>
              <Textarea rows={3} value={seoDescription} onChange={(e) => setSeoDescription(e.target.value.slice(0, 160))} data-testid="blog-editor-seo-description" />
              <div className="text-[10px] text-right text-[#7A5A22] mt-0.5">{seoDescription.length}/160</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="h-7 w-7 inline-flex items-center justify-center rounded text-[#4B5563] hover:bg-white hover:text-[#1F4E3D]"
    >
      {children}
    </button>
  );
}

/** ---------------------------------------------------------------------
 *  Admin Blog list — CRUD landing
 *  --------------------------------------------------------------------- */
export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [editing, setEditing] = useState(null);   // null = list view, {} = new, obj = edit

  const load = useCallback(async () => {
    setPosts(null);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const r = await api.get("/admin/blog/posts", { params });
      setPosts(r.data.items);
      setTotal(r.data.total);
    } catch { setPosts([]); }
  }, [page, statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this blog post? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (editing !== null) {
    return <BlogEditor initial={editing.id ? editing : null} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div data-testid="admin-blog-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl text-[#111827]">Blog Posts</h2>
          <p className="text-sm text-[#4B5563]">Manage insights, guides & SEO content.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2" data-testid="admin-blog-new-button">
          <Plus size={14} /> New post
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search title or slug…"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          className="max-w-xs"
          data-testid="admin-blog-search"
        />
        <NativeSelect
          value={statusFilter}
          onChange={(v) => { setPage(1); setStatusFilter(v); }}
          data-testid="admin-blog-status-filter"
          className="max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </NativeSelect>
      </div>

      <div className="rounded-xl bg-white border border-[#E7DDCF] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5EFE6] text-[#7A5A22] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-2.5">Title</th>
              <th className="text-left px-4 py-2.5">Category</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-left px-4 py-2.5">Views</th>
              <th className="text-left px-4 py-2.5">Updated</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts === null ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#4B5563]">Loading…</td></tr>
            ) : posts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[#4B5563]" data-testid="admin-blog-empty">
                  No posts yet. Click <strong>“New post”</strong> to write your first one.
                </td>
              </tr>
            ) : posts.map((p) => (
              <tr key={p.id} data-testid={`admin-blog-row-${p.slug}`} className="border-t border-[#E7DDCF]/60">
                <td className="px-4 py-3 max-w-[360px]">
                  <div className="font-medium text-[#111827] truncate">{p.title}</div>
                  <div className="text-[11px] text-[#7A5A22] truncate">/blog/{p.slug}</div>
                </td>
                <td className="px-4 py-3 text-[#4B5563]">{p.category}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${p.status === "published" ? "bg-[rgba(31,78,61,0.1)] text-[#1F4E3D]" : "bg-[rgba(122,90,34,0.1)] text-[#7A5A22]"}`} data-testid={`admin-blog-status-${p.slug}`}>
                    {p.status === "published" ? <Eye size={11}/> : <EyeOff size={11}/>}
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#4B5563]">{p.views || 0}</td>
                <td className="px-4 py-3 text-[#4B5563] text-xs">{(p.updated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  {p.status === "published" && (
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-[#1F4E3D] hover:underline"
                      data-testid={`admin-blog-view-${p.slug}`}
                    >
                      <ExternalLink size={12}/> View
                    </a>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`admin-blog-edit-${p.slug}`}>
                    <Pencil size={12} /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(p.id)} data-testid={`admin-blog-delete-${p.slug}`}>
                    <Trash2 size={12} className="text-[#B91C1C]" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="text-sm text-[#4B5563]">Page {page} of {Math.ceil(total / pageSize)}</span>
          <Button variant="outline" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
